import React, { createContext, useCallback } from 'react';
import { Feature, useCapabilities, useIsAdmin, useSendAction } from '@app/api';
import { UnlockFeatureModal } from './UnlockFeatureModal';
import { useModalState } from '@app/core';
import { usePlanId } from './PlanIdContext';

export interface UnlockFeatureContextValue {
  feature?: Feature | boolean;
  setFeature: (feature?: Feature | boolean) => void;
}

export const UnlockFeatureContext = createContext<
  UnlockFeatureContextValue | undefined
>(undefined);

export const useUnlockFeatureContext = () => {
  const context = React.useContext(UnlockFeatureContext);
  if (!context) {
    throw new Error(
      'useUnlockFeatureContext must be used within a UnlockFeatureContextProvider',
    );
  }
  return context;
};

export const useUnlockFeature = (feature?: Feature | boolean) => {
  const context = useUnlockFeatureContext();
  const { mutate } = useSendAction('click_on_locked_feature');
  return useCallback(() => {
    context.setFeature(feature);
    mutate({ feature: feature === true ? 'subscriptions' : feature });
  }, [context, feature, mutate]);
};

const Provider = UnlockFeatureContext.Provider;

interface Props {
  children: React.ReactNode;
}

export const UnlockFeatureContextProvider = ({ children }: Props) => {
  const [feature, setFeature, close] = useModalState<
    Feature | undefined | boolean
  >(undefined);

  return (
    <Provider value={{ feature, setFeature }}>
      {children}
      <UnlockFeatureModal
        isOpen={!!feature}
        onRequestClose={close}
        feature={feature}
      />
    </Provider>
  );
};

export function useFeatureRequiredCallback<
  TCallback extends Function = () => void,
>(feature: Feature | undefined, callback: TCallback, planIdParam?: string) {
  const planId = usePlanId() || planIdParam;

  const unlockFeature = useUnlockFeature(feature);
  const capabilities = useCapabilities(planId);
  const isAdmin = useIsAdmin();

  if (isAdmin || !feature || (capabilities && capabilities[feature])) {
    return callback;
  }
  return unlockFeature as (...params: any[]) => void;
}
